import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getConfiguracionesService,
  createConfiguracionService,
  updateConfiguracionService,
  deleteConfiguracionService,
} from "../../../services/talentos/talentos.service";

// Async thunks
export const loadConfiguraciones = createAsyncThunk(
  "phraseBuilder/loadConfiguraciones",
  async () => {
    const response = await getConfiguracionesService();
    return response;
  }
);

export const saveConfiguration = createAsyncThunk(
  "phraseBuilder/saveConfiguration",
  async ({ frase1Id, frase2Id }, { rejectWithValue }) => {
    try {
      const response = await createConfiguracionService(frase1Id, frase2Id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateConfiguration = createAsyncThunk(
  "phraseBuilder/updateConfiguration",
  async ({ id, frase1Id, frase2Id, orden }, { rejectWithValue }) => {
    try {
      const response = await updateConfiguracionService(id, frase1Id, frase2Id, orden);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteConfiguration = createAsyncThunk(
  "phraseBuilder/deleteConfiguration",
  async (id, { rejectWithValue }) => {
    try {
      await deleteConfiguracionService(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  leftWord: null,
  rightWord: null,

  leftSearch: "",
  rightSearch: "",

  leftRandom: false,
  rightRandom: false,

  leftPhrases: [],
  rightPhrases: [],

  puzzle: {
    left: null,
    right: null,
  },

  saved: [],
  editingId: null,
  loading: false,
  error: null,
};

const phraseBuilderSlice = createSlice({
  name: "phraseBuilder",
  initialState,
  reducers: {
    setLeftWord: (state, action) => {
      state.leftWord = action.payload;
    },
    setRightWord: (state, action) => {
      state.rightWord = action.payload;
    },

    setLeftPhrases: (state, action) => {
      state.leftPhrases = action.payload;
    },
    setRightPhrases: (state, action) => {
      state.rightPhrases = action.payload;
    },

    setLeftRandom: (state, action) => {
      state.leftRandom = action.payload;
    },
    setRightRandom: (state, action) => {
      state.rightRandom = action.payload;
    },

    setLeftSearch: (state, action) => {
      state.leftSearch = action.payload;
    },
    setRightSearch: (state, action) => {
      state.rightSearch = action.payload;
    },

    setPuzzlePiece: (state, action) => {
      const { side, item } = action.payload;
      state.puzzle[side] = item;
    },

    removePuzzlePiece: (state, action) => {
      state.puzzle[action.payload] = null;
    },

    editSaved: (state, action) => {
      const config = state.saved[action.payload];
      // Mapear los datos de la configuración al puzzle
      state.puzzle = {
        left: {
          id: config.frase_1_detalle.id,
          text: config.frase_1_detalle.frase,
          side: "left",
        },
        right: {
          id: config.frase_2_detalle.id,
          text: config.frase_2_detalle.frase,
          side: "right",
        },
      };
      state.editingId = config.id;
    },

    clearPuzzle: (state) => {
      state.puzzle = { left: null, right: null };
      state.editingId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Load configuraciones
      .addCase(loadConfiguraciones.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadConfiguraciones.fulfilled, (state, action) => {
        state.loading = false;
        state.saved = action.payload;
      })
      .addCase(loadConfiguraciones.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Save configuration
      .addCase(saveConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        state.saved.push(action.payload);
        state.puzzle = { left: null, right: null };
        state.editingId = null;
      })
      .addCase(saveConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update configuration
      .addCase(updateConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.saved.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.saved[index] = action.payload;
        }
        state.puzzle = { left: null, right: null };
        state.editingId = null;
      })
      .addCase(updateConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete configuration
      .addCase(deleteConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        state.saved = state.saved.filter((s) => s.id !== action.payload);
      })
      .addCase(deleteConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setLeftWord,
  setRightWord,
  setLeftPhrases,
  setRightPhrases,
  setLeftRandom,
  setRightRandom,
  setLeftSearch,
  setRightSearch,
  setPuzzlePiece,
  removePuzzlePiece,
  editSaved,
  clearPuzzle,
} = phraseBuilderSlice.actions;

export default phraseBuilderSlice.reducer;
